import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CreditCard, KeyRound, RotateCcw, PlugZap, Eye, EyeOff, ShieldCheck, Info, MessageCircle, Copy as CopyIcon } from "lucide-react";

const tones = {
  db: "bg-emerald-50 text-emerald-700 border-emerald-200",
  env: "bg-slate-50 text-slate-600 border-slate-200",
  none: "bg-red-50 text-red-700 border-red-200",
};

export default function AdminPlatformSettings() {
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState({ key_id: "", key_secret: "", mode: "test", webhook_secret: "" });
  const [showSecret, setShowSecret] = useState(false);
  const [showHook, setShowHook] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  // WhatsApp support
  const [supp, setSupp] = useState(null); // {whatsapp_number, whatsapp_wa_link, updated_at}
  const [suppDraft, setSuppDraft] = useState({ whatsapp_number: "", country_code: "91" });
  const [savingSupp, setSavingSupp] = useState(false);

  const load = () => api.get("/admin/platform-settings/razorpay").then((r) => setState(r.data)).catch(() => {});
  const loadSupp = () => api.get("/admin/platform-settings/support").then((r) => {
    setSupp(r.data);
    setSuppDraft({ whatsapp_number: r.data.whatsapp_number || "", country_code: "91" });
  }).catch(() => {});
  useEffect(() => { load(); loadSupp(); }, []);

  const testConn = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/platform-settings/razorpay/test", {
        key_id: draft.key_id || undefined,
        key_secret: draft.key_secret || undefined,
      });
      toast.success(`Razorpay ${data.mode} keys verified`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setTesting(false); }
  };

  const save = async () => {
    if (!draft.key_id.trim() || !draft.key_secret.trim()) return toast.error("Enter both Key ID and Key Secret");
    setSaving(true);
    try {
      await api.patch("/admin/platform-settings/razorpay", draft);
      toast.success("Platform Razorpay keys saved & verified");
      setDraft({ key_id: "", key_secret: "", mode: draft.mode, webhook_secret: "" });
      setShowSecret(false); setShowHook(false);
      await load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Delete the platform Razorpay keys from the database?\n\nThe app will fall back to the env-var keys (if any) for subscription payments and for stores that don't have their own keys.")) return;
    try {
      await api.delete("/admin/platform-settings/razorpay");
      toast.success("Reverted to environment defaults");
      await load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const saveSupport = async () => {
    setSavingSupp(true);
    try {
      const { data } = await api.patch("/admin/platform-settings/support", suppDraft);
      toast.success(data.whatsapp_number ? "Support WhatsApp number saved" : "Support number cleared");
      await loadSupp();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSavingSupp(false); }
  };

  const clearSupport = async () => {
    if (!window.confirm("Remove the support WhatsApp number? Store owners will no longer see the Contact Support button.")) return;
    try {
      await api.patch("/admin/platform-settings/support", { whatsapp_number: "" });
      toast.success("Support number cleared");
      await loadSupp();
    } catch (e) { toast.error(formatError(e)); }
  };

  if (!state) return <div className="p-8 text-slate-400">Loading…</div>;

  const sourceTone = tones[state.source] || tones.none;
  const modeTone = state.active_mode === "live" ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-slate-50 text-slate-600 border-slate-200";
  const sourceLabel = state.source === "db" ? "Custom (this dashboard)" : state.source === "env" ? "Environment fallback" : "Not configured";

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="admin-platform-settings-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Platform settings</h1>
        <p className="text-slate-500">Razorpay credentials used across the whole app.</p>
      </div>

      {/* Info banner */}
      <Card className="border-slate-200 bg-slate-50/60 max-w-3xl">
        <CardContent className="p-4 flex gap-3">
          <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-slate-600 leading-relaxed">
            <div className="font-semibold text-slate-800 mb-1">Where are these keys used?</div>
            <ul className="list-disc ml-4 space-y-1">
              <li><b>Subscription upgrades &amp; renewals only</b> — this is how every shop pays <i>you</i>. Your account, your money.</li>
              <li><b>Not used for print-job payments</b> — every shop must connect its own Razorpay account (or add live keys) to accept customer payments. 100% of each print rupee lands in the shop's own bank.</li>
              <li><b>Webhook secret</b> verifies Razorpay's server-to-server callbacks for subscription payments.</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Currently active */}
      <Card className="border-slate-200 max-w-3xl" data-testid="platform-razorpay-card">
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-orange-500" />
            Razorpay (platform-wide)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3.5" data-testid="platform-active-state">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Currently active</div>
              <div className="flex items-center gap-1.5">
                <Badge variant="outline" className={`text-[10px] ${sourceTone}`}>{sourceLabel}</Badge>
                {state.active_mode && <Badge variant="outline" className={`text-[10px] ${modeTone}`}>{state.active_mode.toUpperCase()}</Badge>}
                {state.webhook_configured && <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">Webhook set</Badge>}
              </div>
            </div>
            <div className="mt-2 font-mono text-sm text-slate-800 break-all" data-testid="platform-active-key">
              {state.active_key_id_masked || <span className="text-red-600 text-xs">Not configured — subscription payments will fail</span>}
            </div>
            {state.updated_at && <div className="text-[11px] text-slate-500 mt-1.5">Last updated {new Date(state.updated_at).toLocaleString()}</div>}
            {!state.env_available && state.source === "none" && (
              <div className="text-[11px] text-red-600 mt-1.5">No env-var fallback available — you must save keys here.</div>
            )}
          </div>

          {/* Editor */}
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Mode</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <Button type="button" size="sm" variant={draft.mode === "test" ? "default" : "outline"} className={draft.mode === "test" ? "bg-slate-800 hover:bg-slate-900" : ""} onClick={() => setDraft({ ...draft, mode: "test" })} data-testid="platform-mode-test">Test</Button>
                <Button type="button" size="sm" variant={draft.mode === "live" ? "default" : "outline"} className={draft.mode === "live" ? "bg-orange-500 hover:bg-orange-600" : ""} onClick={() => setDraft({ ...draft, mode: "live" })} data-testid="platform-mode-live">Live</Button>
                <span className="text-[11px] text-slate-500 ml-1">{draft.mode === "live" ? "Real money · needs rzp_live_ keys" : "Sandbox · needs rzp_test_ keys"}</span>
              </div>
            </div>
            <div>
              <Label className="text-xs">Key ID</Label>
              <Input placeholder={draft.mode === "live" ? "rzp_live_XXXXXXXXXXXX" : "rzp_test_XXXXXXXXXXXX"} value={draft.key_id} onChange={(e) => setDraft({ ...draft, key_id: e.target.value.trim() })} className="font-mono text-sm" data-testid="platform-key-id-input" />
            </div>
            <div>
              <Label className="text-xs">Key Secret</Label>
              <div className="relative">
                <Input type={showSecret ? "text" : "password"} placeholder="••••••••••••••••" value={draft.key_secret} onChange={(e) => setDraft({ ...draft, key_secret: e.target.value })} className="font-mono text-sm pr-10" data-testid="platform-key-secret-input" />
                <button type="button" onClick={() => setShowSecret((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700" aria-label={showSecret ? "Hide" : "Show"}>
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1.5"><ShieldCheck className="w-3 h-3 text-blue-500" />Webhook Secret <span className="text-slate-400 font-normal">(optional)</span></Label>
              <div className="relative">
                <Input type={showHook ? "text" : "password"} placeholder="Optional — leave blank to keep existing" value={draft.webhook_secret} onChange={(e) => setDraft({ ...draft, webhook_secret: e.target.value })} className="font-mono text-sm pr-10" data-testid="platform-webhook-input" />
                <button type="button" onClick={() => setShowHook((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700">
                  {showHook ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="text-[11px] text-slate-400 mt-1">Encrypted at rest. Verifies Razorpay's server-to-server callbacks.</div>
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              <Button type="button" size="sm" variant="outline" onClick={testConn} disabled={testing} data-testid="platform-test-btn">
                <PlugZap className="w-3.5 h-3.5 mr-1.5" />{testing ? "Testing…" : "Test connection"}
              </Button>
              <Button type="button" size="sm" onClick={save} disabled={saving || !draft.key_id || !draft.key_secret} className="bg-orange-500 hover:bg-orange-600" data-testid="platform-save-btn">
                <KeyRound className="w-3.5 h-3.5 mr-1.5" />{saving ? "Saving…" : "Save & verify"}
              </Button>
              {state.has_custom && (
                <Button type="button" size="sm" variant="ghost" onClick={reset} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="platform-reset-btn">
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Revert to env defaults
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Support · WhatsApp */}
      <Card className="border-slate-200 max-w-3xl" data-testid="support-whatsapp-card">
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-500" />
            Support · WhatsApp
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Publish a WhatsApp number your store owners can tap to reach your support team. The store settings page will show a "Contact Support on WhatsApp" button that opens a pre-filled chat with the shop's details attached — no back-and-forth needed to locate their account.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3.5" data-testid="support-active-state">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Currently published</div>
              <Badge variant="outline" className={`text-[10px] ${supp?.whatsapp_number ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                {supp?.whatsapp_number ? "Live" : "Not set"}
              </Badge>
            </div>
            <div className="mt-2 font-mono text-sm text-slate-800 break-all" data-testid="support-active-number">
              {supp?.whatsapp_number ? `+${supp.whatsapp_number}` : <span className="text-red-600 text-xs">Not configured — store owners won't see the support button</span>}
            </div>
            {supp?.updated_at && <div className="text-[11px] text-slate-500 mt-1.5">Last updated {new Date(supp.updated_at).toLocaleString()}</div>}
            {supp?.whatsapp_wa_link && (
              <a href={supp.whatsapp_wa_link} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-800 hover:underline" data-testid="support-preview-link">
                Open chat preview →
              </a>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Country code</Label>
              <Input value={suppDraft.country_code} onChange={(e) => setSuppDraft({ ...suppDraft, country_code: e.target.value.replace(/\D/g, "").slice(0, 3) })} placeholder="91" className="font-mono text-sm w-32" data-testid="support-cc-input" />
              <div className="text-[11px] text-slate-400 mt-1">Digits only, without the "+" (e.g. 91 for India).</div>
            </div>
            <div>
              <Label className="text-xs">WhatsApp number</Label>
              <Input value={suppDraft.whatsapp_number} onChange={(e) => setSuppDraft({ ...suppDraft, whatsapp_number: e.target.value })} placeholder="9876543210" className="font-mono text-sm" data-testid="support-num-input" />
              <div className="text-[11px] text-slate-400 mt-1">Enter the 10-digit local number or the full international number. We'll normalize it to E.164 format.</div>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button type="button" size="sm" onClick={saveSupport} disabled={savingSupp} className="bg-emerald-500 hover:bg-emerald-600" data-testid="support-save-btn">
                <MessageCircle className="w-3.5 h-3.5 mr-1.5" />{savingSupp ? "Saving…" : "Save support number"}
              </Button>
              {supp?.whatsapp_number && (
                <Button type="button" size="sm" variant="ghost" onClick={clearSupport} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="support-clear-btn">
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Remove number
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
